import { useMemo } from 'react';
import mergeWith from 'lodash-es/mergeWith';

import { getCoreRowModel, getExpandedRowModel, useReactTable } from '@tanstack/react-table';
import { safeEmptyArray } from '@kc-console/core';

import {
  CellFallbackValue,
  RowExpanding,
  RowInteraction,
  RowSelection,
  TableCompactMode,
  TableTextEllipsis,
} from './features';

import type { ColumnDef, Row, RowData, TableMeta, Updater } from '@tanstack/react-table';
import type { GetKeyByValueType } from '@kc-console/core';
import type {
  DataTableRowExpandingOptions,
  DataTableRowInteractionOptions,
  DataTableRowSelectionOptions,
} from './features';
import type { ScreenTableSetting } from './useTableSettings';

const INITIAL_STATE: Partial<ScreenTableSetting> = {
  columnOrder: [],
  columnVisibility: {},
  columnSizing: {},
  compactMode: false,
  textEllipsis: true,
};

interface Props<TData extends RowData>
  extends DataTableRowExpandingOptions<TData>,
    DataTableRowInteractionOptions<TData>,
    DataTableRowSelectionOptions<TData> {
  columns: ColumnDef<TData, any>[];
  data: TData[] | undefined | null;
  /**
   * DataTable의 컬럼 리사이징 기능 사용 여부를 설정합니다.
   *
   * @default true
   */
  enableColumnResizing?: boolean;
  /**
   * DataTable의 행 확장 기능 사용 여부를 설정하는 legacy 옵션입니다.
   *
   * @default false
   * @deprecated `enableRowExpanding`을 사용하세요.
   */
  enableExpanding?: boolean;
  /**
   * DataTable의 다중 선택 기능 사용 여부를 설정합니다.
   *
   * - `true`: 다중 선택 가능, 체크박스
   * - `false`: 다중 선택 불가능, 라디오
   *
   * @default true
   */
  enableMultiRowSelection?: boolean | ((row: Row<TData>) => boolean);
  /**
   * DataTable의 선택 기능 사용 여부를 설정합니다.
   *
   * @default true
   */
  enableRowSelection?: boolean | ((row: Row<TData>) => boolean);
  /**
   * 행별로 expand 가능 여부(disabled)를 결정하는 legacy 함수입니다.
   * `enableRowExpanding`을 지정하지 않았을 때만 적용됩니다.
   * - `getSubRows` 있음: tanstack table의 자식 유무 기반 판단을 덮어씁니다.
   * - `getSubRows` 없음: 기본값(`() => enableExpanding`) 대신 이 함수로 행별 disabled를 제어합니다.
   *
   * @example
   * getRowCanExpand: row => row.original.hasDetail
   *
   * @deprecated `enableRowExpanding`의 함수형 옵션을 사용하세요.
   */
  getRowCanExpand?: (row: Row<TData>) => boolean;
  /**
   * 테이블 행의 고유 식별자를 생성하는 방법을 정의합니다.
   *
   * - 함수로 전달하면 해당 함수를 사용하여 각 행의 고유 ID를 생성합니다.
   * - 문자열로 전달하면 해당 속성명을 키로 사용하여 ID를 추출합니다.
   *
   * @example
   * getRowId: 'id' // 문자열 키 사용 예시 (단축 문법)
   * getRowId: (rowData) => rowData.id // 함수 사용 예시
   * getRowId: (rowData) => `${rowData.category}-${rowData.id}` // 복합 ID 생성 예시
   */
  getRowId?:
    | GetKeyByValueType<TData, string>
    | ((originalRow: TData, index: number, parent?: Row<TData> | undefined) => string);
  /**
   * 행의 하위 행(subRow)을 반환하는 함수입니다.
   * 지정 시 tanstack table이 계층형 행 구조를 구성하며, `enableRowExpanding: true` 또는 legacy `enableExpanding: true`와 함께 사용해야 expand 컬럼이 표시됩니다.
   *
   * @example
   * getSubRows: row => row.subRows
   */
  getSubRows?: (originalRow: TData, index: number) => TData[] | undefined;
  initialState?: Partial<ScreenTableSetting>;
  /**
   * 테이블 인스턴스에 커스텀 메타데이터를 전달합니다.
   * 셀 렌더러에서 `table.options.meta`로 접근할 수 있습니다.
   *
   * @example
   * meta: { zoneId: 'zone-123' }
   * // 셀에서: const { zoneId } = table.options.meta as { zoneId: string };
   */
  meta?: Record<string, unknown> & TableMeta<TData>;
  onTableSettingsChange?: (settings: Partial<ScreenTableSetting>) => void;
  /**
   * 셀에서 `data`로부터 렌더링할 값을 찾지 못하였을 때 대체 값을 렌더링합니다. (`undefined`, `null`, `""`)
   */
  renderFallbackValue?: string;
  /**
   * 행 선택 정보
   * 기본값 {}
   */
  rowSelection?: Record<string, boolean>;
  tableCustomSettings?: Partial<ScreenTableSetting>;
  tableSettings?: Partial<ScreenTableSetting>;
}

function mergeScreenTableSettings(...settings: Partial<ScreenTableSetting>[]): ScreenTableSetting {
  return mergeWith({}, ...settings) as ScreenTableSetting;
}

function getRowIdGetter<TData>(
  getRowIdProp:
    | GetKeyByValueType<TData, string>
    | ((originalRow: TData, index: number, parent?: Row<TData> | undefined) => string)
    | undefined,
) {
  if (typeof getRowIdProp === 'function') {
    return getRowIdProp;
  } else if (typeof getRowIdProp === 'string') {
    return (rowData: TData) => rowData[getRowIdProp] as string;
  }
  return undefined;
}

export default function useDataTable<TData extends RowData>({
  data,
  columns,
  enableColumnResizing = true,
  enableRowSelection = true,
  enableRowInteraction = true,
  enableMultiRowSelection = true,
  enableExpanding = false,
  enableRowExpanding,
  getRowId: getRowIdProp,
  getRowCanExpand,
  getSubRows,
  hideRowExpanding,
  hideRowSelection,
  meta,
  renderFallbackValue = '-',
  tableSettings = {},
  tableCustomSettings = {},
  onTableSettingsChange,
  rowSelection = {},
  initialState = INITIAL_STATE,
}: Props<TData>) {
  const safeData = useMemo(() => safeEmptyArray(data), [data]);

  const initialTableState = useMemo(
    () => mergeScreenTableSettings(INITIAL_STATE, initialState),
    [initialState],
  );

  const mergedSettings = useMemo(
    () => mergeScreenTableSettings(initialTableState, tableSettings, tableCustomSettings),
    [initialTableState, tableSettings, tableCustomSettings],
  );

  const {
    columnOrder = [],
    columnPinning = {},
    columnSizing = {},
    columnVisibility = {},
    compactMode = false,
    textEllipsis = true,
  } = mergedSettings;

  const getRowId = getRowIdGetter<TData>(getRowIdProp);

  const resolvedEnableExpanding =
    enableRowExpanding === undefined ? enableExpanding : enableRowExpanding !== false;
  const createUpdater =
    <TKey extends keyof ScreenTableSetting>(
      key: TKey,
      currentValue: Required<ScreenTableSetting>[TKey],
    ) =>
    (updater: Updater<Required<ScreenTableSetting>[TKey]>) => {
      const newValue = typeof updater === 'function' ? updater(currentValue) : updater;
      return onTableSettingsChange?.({ [key]: newValue });
    };

  const tableInstance = useReactTable({
    columns,
    data: safeData,
    columnResizeMode: 'onChange',
    enableColumnResizing,
    enableExpanding: resolvedEnableExpanding,
    enableMultiRowSelection,
    enableRowExpanding,
    enableRowInteraction,
    enableRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getSubRows ? getExpandedRowModel() : undefined,
    getRowId: getRowId,
    getRowCanExpand,
    getSubRows,
    defaultColumn: {
      minSize: 120,
      size: 200,
    },
    hideRowExpanding,
    hideRowSelection,
    meta,
    initialState: {
      ...initialTableState,
      rowSelection,
    },
    state: {
      columnOrder,
      columnPinning,
      columnSizing,
      columnVisibility,
      compactMode,
      textEllipsis,
    },
    onColumnOrderChange: createUpdater('columnOrder', columnOrder),
    onColumnSizingChange: createUpdater('columnSizing', columnSizing),
    onColumnVisibilityChange: createUpdater('columnVisibility', columnVisibility),
    onCompactModeChange: createUpdater('compactMode', compactMode),
    onTextEllipsisChange: createUpdater('textEllipsis', textEllipsis),
    renderFallbackValue,
    _features: [
      CellFallbackValue,
      RowSelection,
      RowExpanding,
      RowInteraction,
      TableCompactMode,
      TableTextEllipsis,
    ],
  });

  return {
    tableInstance,
  };
}
